/**
 * Owned-channel statistics: followers, growth, reach.
 *
 * One document per channel in `skidaCrmChannelStats`, id = the channel name
 * from `CHANNELS`, so the six slots on the Channel stats screen map one-to-one
 * onto six documents and a missing one is a channel we have not connected yet.
 *
 * ## Written only by the collector
 *
 * Every field here comes from a platform API called server-side, because no
 * channel API can be called from a browser: the tokens would be readable by
 * anyone with the page open, and Meta and TikTok both bind a token to an app
 * secret that must never leave the server. The client reads; a scheduled Cloud
 * Function writes. The Firestore rules enforce exactly that.
 *
 * ## Everything is nullable, deliberately
 *
 * Not every platform exposes every number, and the ones that do change what
 * they expose. A missing metric renders as "—" rather than as 0, because a zero
 * that means "the API stopped returning this" and a zero that means "nobody
 * watched" would be indistinguishable — and the second is a decision the
 * marketing team would act on.
 */
import type { Channel } from './posts'

export interface ChannelStats {
  /** Document id, and one of `CHANNELS`. */
  channel: Channel

  /** Followers / subscribers right now. */
  followers: number | null
  /**
   * Change over the last 30 days, in followers. Signed: negative is a real
   * answer and must survive to the screen.
   *
   * Derived by the collector from its own history rather than from the
   * platform, because only YouTube reports a delta and the others would each
   * mean something slightly different by it.
   */
  followersChange30d: number | null

  /** Views / impressions over the last 30 days. */
  views30d: number | null
  /** Posts published in the last 30 days. */
  posts30d: number | null

  /** Public profile, for the "View profile ↗" link. */
  profileUrl: string

  /**
   * One follower reading per day, oldest first, up to 35 days.
   *
   * The series is what makes a trend possible at all: no platform reports
   * "followers a week ago", so the only history that exists is the history we
   * keep. It starts the day a channel is connected, which is why a new channel
   * shows a number and no direction.
   */
  samples: { at: number; followers: number }[]

  /** When the collector last wrote this document. */
  fetchedAt: string | null
  /**
   * Set when the last collection attempt failed. The previous numbers stay
   * visible with this alongside them — stale data plus a reason beats a blank
   * card, and beats silently showing month-old figures as if they were current.
   */
  error: string
}

/** A channel with no document yet: connected to nothing, and says so. */
export const UNCONNECTED: Omit<ChannelStats, 'channel'> = {
  followers: null,
  followersChange30d: null,
  views30d: null,
  posts30d: null,
  profileUrl: '',
  samples: [],
  fetchedAt: null,
  error: '',
}

/**
 * How stale is too stale to present without comment.
 *
 * The collector runs daily. Two days means one run was missed, which is worth
 * saying on the card — a follower count nobody has refreshed in a week looks
 * exactly like one that is simply not growing.
 */
export const STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000
